import { useEffect, useState } from "react";

import type { EchoflicksSessionMedia } from "../types";
import { isHeic } from "./sessionUtils";
import { useMediaUrl } from "./useMediaUrl";

export type DisplayableImageState = {
  /** A URL the browser can actually render in <img> (HEIC already converted). */
  url: string | null;
  status: "idle" | "loading" | "loaded" | "error";
  isLoading: boolean;
  error?: string;
  refresh: () => void;
};

// Converted HEIC object URLs, keyed by the source storage path, so we never
// re-decode the same photo when a card remounts or its signed URL refreshes
// (libheif decoding is CPU-heavy). Object URLs live for the tab's lifetime —
// bounded by the number of distinct HEIC photos an admin actually opens.
const convertedHeicUrls = new Map<string, string>();
const decodedImagePreloads = new Map<string, Promise<void>>();
const maxDecodedImagePreloads = 100;

/** Warm the browser's byte and decode caches without retaining image bitmaps. */
export const preloadDisplayableImage = (url: string): Promise<void> => {
  const existing = decodedImagePreloads.get(url);
  if (existing) return existing;
  if (typeof Image === "undefined") return Promise.resolve();

  let image: HTMLImageElement | null = new Image();
  image.decoding = "async";
  image.src = url;
  const decode = image.decode
    ? image.decode()
    : new Promise<void>((resolve, reject) => {
        image!.onload = () => resolve();
        image!.onerror = () => reject(new Error("Could not preload image"));
      });
  const preload = decode
    .then(() => undefined)
    .catch((error) => {
      if (decodedImagePreloads.get(url) === preload) {
        decodedImagePreloads.delete(url);
      }
      throw error;
    })
    .finally(() => {
      image = null;
    });
  decodedImagePreloads.set(url, preload);
  while (decodedImagePreloads.size > maxDecodedImagePreloads) {
    const oldest = decodedImagePreloads.keys().next().value as
      | string
      | undefined;
    if (!oldest) break;
    decodedImagePreloads.delete(oldest);
  }
  return preload;
};

export const clearDisplayableImagePreloadCacheForTests = () => {
  decodedImagePreloads.clear();
};

function mediaKey(
  media: EchoflicksSessionMedia | null | undefined,
): string | null {
  return media?.storage_path ?? media?.s3_key ?? media?.url ?? null;
}

/**
 * Resolve a signed URL for a media item and, when it is a HEIC/HEIF photo
 * (iPhone default that browsers can't decode), convert it to a JPEG object URL
 * client-side so it renders in an <img>. Non-HEIC media passes through
 * `useMediaUrl` unchanged.
 */
export function useDisplayableImage(
  media: EchoflicksSessionMedia | null | undefined,
): DisplayableImageState {
  const base = useMediaUrl(media ?? undefined);
  const heic = isHeic(media?.mime_type, mediaKey(media));
  const key = mediaKey(media);

  const [converted, setConverted] = useState<string | null>(() =>
    heic && key ? (convertedHeicUrls.get(key) ?? null) : null,
  );
  const [convStatus, setConvStatus] = useState<"idle" | "loading" | "error">(
    "idle",
  );

  useEffect(() => {
    if (!heic) {
      setConverted(null);
      setConvStatus("idle");
      return;
    }
    if (key && convertedHeicUrls.has(key)) {
      setConverted(convertedHeicUrls.get(key)!);
      setConvStatus("idle");
      return;
    }
    const source = base.url;
    if (!source) return;

    let active = true;
    setConvStatus("loading");
    (async () => {
      try {
        const response = await fetch(source);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const { default: heic2any } = await import("heic2any");
        const out = await heic2any({
          blob,
          toType: "image/jpeg",
          quality: 0.85,
        });
        const jpeg = Array.isArray(out) ? out[0] : out;
        const objectUrl = URL.createObjectURL(jpeg);
        if (!active) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        if (key) convertedHeicUrls.set(key, objectUrl);
        setConverted(objectUrl);
        setConvStatus("idle");
      } catch {
        if (active) setConvStatus("error");
      }
    })();

    return () => {
      active = false;
    };
  }, [heic, key, base.url]);

  if (!heic) {
    return {
      url: base.url,
      status: base.status,
      isLoading: base.isLoading,
      error: base.error,
      refresh: base.refresh,
    };
  }

  const status: DisplayableImageState["status"] =
    convStatus === "error"
      ? "error"
      : converted
        ? "loaded"
        : base.isLoading || convStatus === "loading"
          ? "loading"
          : base.status;

  return {
    url: converted,
    status,
    isLoading: base.isLoading || convStatus === "loading",
    error:
      convStatus === "error"
        ? "Could not convert this HEIC photo for preview."
        : base.error,
    refresh: base.refresh,
  };
}
